import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Sparkles, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { useInView } from '../../hooks/useInView';
import { pricingPlans } from '../../data/mock';

const PricingCard = ({ plan, index, onRequestReview }) => {
  const [ref, isInView] = useInView();
  const isPopular = plan.popular;
  const isHighlight = plan.highlight;

  return (
    <div
      ref={ref}
      className={`relative flex flex-col rounded-xl border p-7 transition-[opacity,transform,border-color,box-shadow] duration-500 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        } ${isPopular
          ? 'border-amber-400 bg-white shadow-xl shadow-amber-500/10 scale-[1.02] z-10'
          : isHighlight
            ? 'border-stone-800 bg-stone-950 text-white'
            : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow-md'
        }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Popular badge */}
      {isPopular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <Badge className="bg-amber-500 text-white border-0 px-4 py-1 text-xs font-semibold shadow-md">
            Most Popular
          </Badge>
        </div>
      )}

      {/* Highlight badge */}
      {isHighlight && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <Badge className="bg-stone-700 text-stone-100 border-0 px-4 py-1 text-xs font-semibold">
            <Sparkles className="w-3 h-3 mr-1" />
            Premium
          </Badge>
        </div>
      )}

      {/* Plan header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <h3
            className={`text-xl font-bold ${isHighlight ? 'text-stone-100' : 'text-stone-900'
              }`}
          >
            {plan.name}
          </h3>
          <span
            className={`text-xs font-mono font-medium px-2 py-0.5 rounded-md ${isHighlight
              ? 'bg-stone-800 text-stone-300'
              : 'bg-stone-100 text-stone-500'
              }`}
          >
            {plan.period}
          </span>
        </div>
        <p
          className={`text-sm mb-5 ${isHighlight ? 'text-stone-400' : 'text-stone-500'
            }`}
        >
          {plan.description}
        </p>

        <div className="flex items-baseline gap-1">
          <span
            className={`text-4xl font-extrabold tracking-tight ${isHighlight ? 'text-white' : 'text-stone-900'
              }`}
          >
            {plan.price}
          </span>
          <span
            className={`text-lg font-medium ${isHighlight ? 'text-stone-400' : 'text-stone-500'
              }`}
          >
            {plan.currency}
          </span>
        </div>
        <p
          className={`text-xs mt-1 font-mono ${isHighlight ? 'text-stone-500' : 'text-stone-400'
            }`}
        >
          Up to {plan.lineLimit} lines
        </p>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-1">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <Check
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular
                ? 'text-amber-500'
                : isHighlight
                  ? 'text-amber-400'
                  : 'text-stone-400'
                }`}
            />
            <span
              className={`text-sm ${isHighlight ? 'text-stone-300' : 'text-stone-600'
                }`}
            >
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Button
        onClick={() => onRequestReview(plan)}
        className={`w-full h-11 text-sm font-semibold transition-[background-color,box-shadow] duration-200 ${isPopular
          ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-500/20'
          : isHighlight
            ? 'bg-white hover:bg-stone-100 text-stone-900'
            : 'bg-stone-900 hover:bg-stone-800 text-white'
          }`}
      >
        Request Review
        <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </div>
  );
};

export const PricingSection = () => {
  const [titleRef, titleInView] = useInView();
  const [customRef, customInView] = useInView();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestReview = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setIsModalOpen(false);
        navigate('/success');
      }, 3000);
    }
  };

  return (
    <section id="pricing" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ease-out ${titleInView
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-6'
            }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3 block">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
            Choose Your Review Plan
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto text-lg">
            Transparent pricing based on code complexity. Each plan includes
            expert review, annotated feedback, and actionable recommendations.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5 mb-16">
          {pricingPlans.map((plan, i) => (
            <PricingCard key={plan.id} plan={plan} index={i} onRequestReview={handleRequestReview} />
          ))}
        </div>

        {/* Custom quote CTA */}
        <div
          ref={customRef}
          className={`text-center transition-[opacity,transform] duration-700 ease-out ${customInView
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-6'
            }`}
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-xl bg-stone-50 border border-stone-200">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-stone-500" />
              <span className="text-sm text-stone-600 font-medium">
                Need a custom audit for a larger codebase?
              </span>
            </div>
            <Link to="/custom-quote">
              <Button
                variant="outline"
                className="border-stone-300 text-stone-700 hover:bg-white font-medium"
              >
                Request a Custom Quote
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Request {selectedPlan?.name} Review</DialogTitle>
            <DialogDescription>
              Please enter your email address to be contacted. We'll get back to you shortly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleModalSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button disabled={isLoading} type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
